import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { usePermissions } from "../../Auth/PermissionsContext";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { formatRelativeTime } from "../../Helper/dates";
import { UserAvatar } from "../UserAvatar";
import { TicketDetailModal } from "../Tickets/TicketDetailModal";
import { ticketsClient, type Ticket, type TicketCategory } from "../../Pages/Tickets/ticketsClient";

/**
 * Top-of-editor banner listing the OPEN tickets linked to this content, so an editor sees outstanding
 * issues before touching the document. Compact cards (status + title + creator avatar) linking to the
 * Tickets page. Hidden when there are none. Gated by feature:tickets:read; refreshes on any ticket event.
 */
export const LinkedTicketsBanner: React.FC<{ contentId?: string | null; className?: string }> = ({ contentId, className = "" }) => {
	const { can } = usePermissions();
	const canRead = can("feature:tickets:read");
	const [tickets, setTickets] = useState<Ticket[]>([]);
	const [categories, setCategories] = useState<TicketCategory[]>([]);
	// Clicking a linked ticket opens it in a read-only modal so the editor stays on this content.
	const [openTicketId, setOpenTicketId] = useState<string | null>(null);

	const categoryLabel = useMemo(() => {
		const map = new Map(categories.map((c) => [c.key, c.label]));
		return (key: string) => map.get(key) ?? key;
	}, [categories]);

	const load = useCallback(async () => {
		if (!contentId || !canRead) {
			setTickets([]);
			return;
		}
		try {
			const res = await ticketsClient.list({ linkedContentId: contentId, status: "open", pageSize: 20 });
			setTickets(res.items);
		} catch {
			setTickets([]);
		}
	}, [contentId, canRead]);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (canRead) ticketsClient.categories().then(setCategories).catch(() => setCategories([]));
	}, [canRead]);

	useRealtimeEvent("ticket", () => load());

	if (!contentId || !canRead || tickets.length === 0) return null;

	return (
		<div className={`rounded-xl border border-amber-200 bg-amber-50/60 p-3 ${className}`}>
			<div className="mb-2 flex items-center gap-2 text-amber-800">
				<FiAlertCircle className="h-4 w-4 shrink-0" />
				<h3 className="text-sm font-semibold">
					{tickets.length} ticket{tickets.length > 1 ? "s" : ""} ouvert{tickets.length > 1 ? "s" : ""} sur ce contenu
				</h3>
			</div>
			<div className="grid gap-1.5 sm:grid-cols-2">
				{tickets.map((t) => (
					<button
						key={t.id}
						type="button"
						onClick={() => setOpenTicketId(t.id)}
						className="flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-left transition hover:border-amber-300 hover:bg-amber-50"
						title={t.title}
					>
						<UserAvatar
							name={t.createdBy?.name ?? t.createdByEmail}
							id={t.createdBy?.id ?? undefined}
							avatarKey={t.createdBy?.avatarKey}
							pictureUrl={t.createdBy?.pictureUrl}
							sizeClass="h-7 w-7"
							textClass="text-[10px]"
							title={`Créé par ${t.createdBy?.name ?? t.createdByEmail}`}
						/>
						<span className="min-w-0 flex-1">
							<span className="block truncate text-sm font-semibold text-slate-800">{t.title}</span>
							<span className="block truncate text-[11px] text-slate-400">{formatRelativeTime(t.updatedAt)}</span>
						</span>
					</button>
				))}
			</div>

			<TicketDetailModal ticketId={openTicketId} categoryLabel={categoryLabel} onClose={() => setOpenTicketId(null)} />
		</div>
	);
};

export default LinkedTicketsBanner;
